import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QrCode, Sun, MapPin, Plane, Building2, Building, Ship, TrainFront, Hotel, Stamp, Shield, Tag, ArrowRight, Compass, Search, Waypoints, Clock } from "lucide-react";
import ShareBoard from "../ShareBoard";
import { pickAirportBg } from "../../lib/assets";
import api from "../../lib/api";

/* Home top banner: LEFT = photo-real city hero + colorful board cards + nearby airports.
   RIGHT = "My Social Pulse™" promo card cycling through 6 real app screens. */

const HERO_SCREENS = [
  { type: "img",    src: "https://customer-assets-rejwkqb3.emergentagent.net/job_flight-network-1/artifacts/jtefvmmv_app-home-1-plain.png", label: "Home" },
  { type: "iframe", src: "/msp/trips",         label: "Trips" },
  { type: "iframe", src: "/msp/plan",          label: "Plan" },
  { type: "iframe", src: "/msp/hotels",        label: "Hotels" },
  { type: "iframe", src: "/msp/documents",     label: "Documents" },
  { type: "iframe", src: "/msp/ai-assistant",  label: "AI Assistant" },
];

const BOARDS = [
  { to: "/airlines",   label: "Airlines",   Icon: Plane,      tint: "from-sky-100 to-white",      ic: "text-sky-600" },
  { to: "/airports",   label: "Airports",   Icon: Building2,  tint: "from-blue-100 to-white",     ic: "text-blue-700" },
  { to: "/city-guides",label: "Cities",     Icon: Building,   tint: "from-fuchsia-100 to-white",  ic: "text-fuchsia-600" },
  { to: "/water",      label: "Cruises",    Icon: Ship,       tint: "from-indigo-100 to-white",   ic: "text-indigo-600" },
  { to: "/rail",       label: "Trains",     Icon: TrainFront, tint: "from-orange-100 to-white",   ic: "text-orange-600" },
  { to: "/stays",      label: "Hotels",     Icon: Hotel,      tint: "from-emerald-100 to-white",  ic: "text-emerald-600" },
  { to: "/visas",      label: "Visas",      Icon: Stamp,      tint: "from-violet-100 to-white",   ic: "text-violet-600" },
  { to: "/features#insurance", label: "Insurance", Icon: Shield, tint: "from-teal-100 to-white", ic: "text-teal-600" },
  { to: "/loyalty",    label: "Deals",      Icon: Tag,        tint: "from-amber-100 to-white",    ic: "text-amber-600" },
];

const FALLBACK_NEARBY = [
  { code: "PDX", name: "Portland Intl",         city: "Portland, OR",     distance_mi: 12,   drive_min: 20,   to: "/airports/PDX" },
  { code: "SEA", name: "Seattle-Tacoma Intl",   city: "Seattle, WA",       distance_mi: 173,  drive_min: 175,  to: "/airports/SEA" },
  { code: "SFO", name: "San Francisco Intl",    city: "San Francisco, CA", distance_mi: 550,  drive_min: 605,  to: "/airports/SFO" },
  { code: "LAX", name: "Los Angeles Intl",      city: "Los Angeles, CA",   distance_mi: 830,  drive_min: 990,  to: "/airports/LAX" },
  { code: "SJC", name: "San José Mineta Intl",  city: "San José, CA",      distance_mi: 585,  drive_min: 640,  to: "/airports/SJC" },
  { code: "JFK", name: "John F. Kennedy Intl",  city: "New York, NY",      distance_mi: 2440, flight_min: 320, to: "/airports/JFK" },
];

function useCurrentCity() {
  const [city, setCity] = useState({ name: "Portland", region: "Oregon", country: "USA" });
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      const map = {
        "America/Los_Angeles": { name: "Portland", region: "Oregon", country: "USA" },
        "America/New_York":    { name: "New York", region: "New York", country: "USA" },
        "America/Chicago":     { name: "Chicago",  region: "Illinois", country: "USA" },
        "Europe/London":       { name: "London",   region: "England",  country: "UK"  },
        "Asia/Kolkata":        { name: "Bengaluru",region: "Karnataka",country: "India" },
        "Asia/Tokyo":          { name: "Tokyo",    region: "Kanto",    country: "Japan" },
      };
      if (map[tz]) setCity(map[tz]);
    } catch { /* silent */ }
  }, []);
  return city;
}

function useCityPhoto(city) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let ignore = false;
    const query = `${city.name} ${city.country} city skyline`;
    api.get("/unsplash/search", { params: { q: query, per_page: 1 } })
      .then((r) => {
        if (ignore) return;
        const first = (r.data?.results || r.data?.items || [])[0];
        const url = first?.url_regular || first?.urls?.regular || first?.image_url || first?.url;
        if (url) setSrc(url);
      }).catch(() => {});
    return () => { ignore = true; };
  }, [city.name, city.country]);
  return src || pickAirportBg(city.name + city.country);
}

function useNearbyAirports(cityName) {
  const [items, setItems] = useState(FALLBACK_NEARBY);
  useEffect(() => {
    api.get(`/city/${encodeURIComponent(cityName)}/summary`)
      .then((r) => {
        const list = r.data?.nearby_airports;
        if (Array.isArray(list) && list.length) setItems(list);
      }).catch(() => {});
  }, [cityName]);
  return items;
}

function fmtDistance(mi) {
  if (mi == null) return "";
  return mi < 100 ? `${mi} mi` : `${Math.round(mi)} mi`;
}

function fmtDuration(a) {
  if (a.flight_min) {
    const h = Math.floor(a.flight_min / 60);
    const m = a.flight_min % 60;
    return `${h}h ${m}m flight`;
  }
  if (a.drive_min) {
    if (a.drive_min < 60) return `${a.drive_min} min drive`;
    const h = Math.floor(a.drive_min / 60);
    const m = a.drive_min % 60;
    return m ? `${h}h ${m}m drive` : `${h}h drive`;
  }
  return "";
}

export default function MspTopBanner() {
  const [i, setI] = useState(0);
  const city = useCurrentCity();
  const heroBg = useCityPhoto(city);
  const nearbyAirports = useNearbyAirports(city.name);
  const mountedRef = useRef(new Set([0]));

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % HERO_SCREENS.length), 5500);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { mountedRef.current.add(i); }, [i]);

  const qrData = typeof window !== "undefined" ? window.location.origin + "/msp" : "https://travelerpulse.pro/msp";

  return (
    <section className="grid lg:grid-cols-[1.1fr_1fr] gap-5 lg:gap-6 lg:items-start" data-testid="home-msp-banner">
      {/* LEFT: rich city hero + boards + nearby airports */}
      <div className="space-y-4">
        <div className="relative overflow-hidden rounded-3xl border border-white shadow-xl min-h-[260px] flex flex-col justify-end bg-slate-800" data-testid="home-current-city">
          <img src={heroBg} alt={`${city.name} skyline`} loading="eager"
            className="absolute inset-0 w-full h-full object-cover" />
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-slate-900/20" />
          <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-xs font-700 text-amber-700 shadow">
            <Sun className="w-4 h-4" /> Sunny 68°F
          </div>
          <div className="relative p-5 sm:p-6 text-white">
            <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-amber-200 drop-shadow-sm">
              Plan · Share · Explore · Belong
            </div>
            <div className="mt-2 flex items-end gap-2">
              <MapPin className="w-5 h-5 text-amber-300 mb-1.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-serif font-800 text-3xl sm:text-4xl leading-[1.05] tracking-tight drop-shadow-md">
                  Discover {city.name}
                </div>
                <div className="text-sm text-white/85 mt-1">
                  {city.region} · {city.country}
                </div>
              </div>
            </div>
            <form className="mt-4 flex items-center gap-2 rounded-full bg-white/95 shadow-lg pl-4 pr-1 py-1"
              onSubmit={(e) => { e.preventDefault(); const q = e.target.elements.q.value.trim(); if (q) window.location.href = `/search?q=${encodeURIComponent(q)}`; }}>
              <Search className="w-4 h-4 text-slate-400 shrink-0" />
              <input name="q" data-testid="home-city-search" placeholder={`Search in ${city.name} — places, hotels, tips...`}
                className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400 py-2" />
              <button data-testid="home-city-search-submit"
                className="shrink-0 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-700 text-xs px-4 py-2">
                Search
              </button>
            </form>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3.5" data-testid="home-boards">
          {BOARDS.map(({ to, label, Icon, tint, ic }, idx) => {
            const wide = idx === BOARDS.length - 1;
            return (
              <div key={label}
                className={`relative group rounded-2xl bg-gradient-to-br ${tint} border border-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-5 min-h-[140px] flex flex-col justify-between ${wide ? "col-span-2 min-h-[110px]" : ""}`}>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ShareBoard url={to} title={`${label} · My Social Pulse™`} text={`Discover ${label} on My Social Pulse™`}
                    testid={`home-board-${label.toLowerCase()}-share`} />
                </div>
                <Link to={to} data-testid={`home-board-${label.toLowerCase()}`}
                  className={`contents ${wide ? "!flex flex-row items-center gap-4" : ""}`}>
                  <span className={`w-12 h-12 rounded-2xl bg-white ${ic} grid place-items-center shadow-sm shrink-0`}>
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </span>
                  <div className={wide ? "flex-1 min-w-0" : ""}>
                    <div className="font-serif font-800 text-lg text-slate-900 leading-tight">{label}</div>
                    {wide && <div className="text-xs text-slate-600 mt-0.5">Today's featured deals →</div>}
                  </div>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Nearby airports — 2 rows × 3 tiles with distance / drive/flight time */}
        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="home-nearby-airports">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 grid place-items-center">
                <Waypoints className="w-4 h-4" />
              </span>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-emerald-700">Near {city.name}</div>
                <div className="font-serif font-800 text-base text-blue-900 leading-tight">Airports for your next trip</div>
              </div>
            </div>
            <Link to="/airports" data-testid="home-nearby-airports-more"
              className="inline-flex items-center gap-1 text-blue-700 font-700 hover:underline text-xs">
              All airports <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-px bg-slate-100">
            {nearbyAirports.slice(0, 6).map((a) => (
              <Link key={a.code} to={a.to} data-testid={`home-airport-${a.code.toLowerCase()}`}
                className="relative min-h-[150px] group overflow-hidden bg-white">
                <img src={pickAirportBg(a.code + a.city)} alt={a.name} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-slate-900/92 via-slate-900/45 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-3 text-white">
                  <div className="font-serif font-800 text-xl leading-none tracking-tight">{a.code}</div>
                  <div className="text-[11px] text-white/90 mt-1 leading-tight truncate">{a.name}</div>
                  <div className="text-[10px] text-white/70 truncate">{a.city}</div>
                  {(a.distance_mi != null || a.drive_min || a.flight_min) && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-amber-200 font-700">
                      <Clock className="w-3 h-3" />
                      <span>{fmtDistance(a.distance_mi)}{a.distance_mi != null && (a.drive_min || a.flight_min) ? " · " : ""}{fmtDuration(a)}</span>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT: MSP App promo with 6-screen live rotator (no store badges yet) */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white shadow-xl flex flex-col" data-testid="home-msp-promo">
        <div aria-hidden="true" className="absolute -top-14 -right-14 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
        <div aria-hidden="true" className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-amber-300/20 blur-2xl" />

        <Link to="/msp" className="relative p-5 sm:p-6 flex items-start justify-between gap-3 hover:brightness-110">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] font-mono uppercase tracking-widest">
              <Compass className="w-3 h-3" /> My Social Pulse™
            </div>
            <div className="font-serif font-800 text-2xl sm:text-3xl leading-tight mt-2.5">
              Explore. Plan.<br />Share. Travel.
            </div>
            <div className="text-xs sm:text-sm text-blue-100/90 mt-1.5 leading-relaxed max-w-[92%]">
              Your complete travel life — organized in one place.
            </div>
          </div>
          <div className="inline-flex items-center gap-1 text-xs font-700 text-amber-200 shrink-0 pt-1">
            Learn more <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </Link>

        <div className="relative mx-5 sm:mx-6 rounded-2xl overflow-hidden bg-white shadow-2xl" data-testid="home-msp-app-screen">
          <div className="relative w-full" style={{ aspectRatio: "9 / 16.4" }}>
            {HERO_SCREENS.map((screen, idx) => {
              const active = idx === i;
              const shouldMount = mountedRef.current.has(idx) || active;
              return (
                <div key={screen.label}
                  className={`absolute inset-0 transition-opacity duration-700 ${active ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                  data-testid={`home-msp-screen-${screen.label.toLowerCase().replace(/\s+/g,"-")}`}>
                  {shouldMount && screen.type === "img" && (
                    <img src={screen.src} alt={`My Social Pulse ${screen.label} preview`}
                      className="w-full h-full object-cover object-top" />
                  )}
                  {shouldMount && screen.type === "iframe" && (
                    <iframe src={screen.src} loading="lazy" title={`My Social Pulse ${screen.label}`}
                      className="w-full h-full border-0 pointer-events-none" scrolling="no" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative px-5 sm:px-6 pt-3 flex items-center justify-center gap-1.5" data-testid="home-msp-screen-dots">
          {HERO_SCREENS.map((s, idx) => (
            <button key={s.label} onClick={() => setI(idx)} aria-label={`Show ${s.label}`}
              data-testid={`home-msp-dot-${idx}`}
              className={`h-2 rounded-full transition-all ${idx === i ? "w-8 bg-amber-300" : "w-2 bg-white/40 hover:bg-white/60"}`} />
          ))}
        </div>

        {/* QR-only CTA row — App Store / Google Play buttons intentionally hidden until launch */}
        <div className="relative p-5 sm:p-6 flex items-center gap-4 mt-auto">
          <img alt="QR to install" className="w-16 h-16 rounded-xl border-2 border-white bg-white p-0.5 shrink-0"
            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(qrData)}`} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1 text-xs font-700 text-amber-100"><QrCode className="w-3.5 h-3.5" /> Scan to preview</div>
            <div className="text-[11px] text-blue-100/80 mt-0.5 leading-snug">
              Native iOS & Android launching soon — try the web preview now.
            </div>
          </div>
          <Link to="/msp" data-testid="home-msp-open-preview"
            className="shrink-0 rounded-full bg-amber-300 text-slate-900 text-xs font-800 px-4 py-2 hover:brightness-105">
            Open preview
          </Link>
        </div>
      </div>
    </section>
  );
}
